import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  useV2ListProjectServicesQuery,
  V2ProjectServiceResource,
  V2ProjectSubserviceResource,
} from "@generated";
import type {
  Section,
  SectionOption,
} from "@components/NestedItemSelect/NestedItemSelect";
import {
  LineOption,
  serviceOptionId,
  subserviceOptionId,
} from "./lineSelection";

const col = (id: string, header: string): ColumnDef<SectionOption> => ({
  id,
  header,
  cell: (props) => String(props.row.original[id] ?? ""),
});

const columns = [
  col("name", "Name"),
  col("quantity", "QTY"),
  col("hours", "Hours"),
];

const SECTIONS = [
  {
    id: "professional-services",
    name: "Professional Services",
    serviceType: "professional_services",
  },
  {
    id: "managed-services",
    name: "Managed Services",
    serviceType: "managed_services",
  },
];

const hours = (v: unknown) => String(v || "0.0");

// Builds the parent-line picker: one section per service type, each a flat
// list of service rows followed by their active subservice rows. Nothing
// starts selected; an empty selection is valid.
export const useParentLines = (projectId: string) => {
  const { data, isFetching, error } = useV2ListProjectServicesQuery({
    filter: { project: projectId },
    include: ["project-subservices"],
    page: { size: 500 },
  });

  const sections: Section[] = useMemo(() => {
    const services = (data?.data ?? []) as V2ProjectServiceResource[];
    const included = (data?.included ?? []) as V2ProjectSubserviceResource[];

    const toOptions = (service: V2ProjectServiceResource): LineOption[] => {
      const serviceId = String(service.id);
      const subs = (service.relationships?.["project-subservices"]?.data ?? [])
        .map((ref) =>
          included.find(
            (item) => item.type === "project-subservices" && item.id === ref.id
          )
        )
        //@ts-ignore active isn't in the generated subservice attributes
        .filter((sub) => sub?.attributes?.active === true)
        .map(
          (sub): LineOption => ({
            id: subserviceOptionId(String(sub!.id)),
            name: `  ↳ ${sub!.attributes?.name ?? ""}`,
            selected: false,
            kind: "subservice",
            serviceId,
            hours: hours(sub!.attributes?.["override-hours"]),
            quantity: Number(sub!.attributes?.quantity ?? 1),
          })
        );
      return [
        {
          id: serviceOptionId(serviceId),
          name: service.attributes?.name ?? "",
          selected: false,
          kind: "service",
          serviceId,
          hours: hours(service.attributes?.["override-hours"]),
          quantity: Number(service.attributes?.quantity ?? 1),
        },
        ...subs,
      ];
    };

    return SECTIONS.map(({ id, name, serviceType }) => ({
      id,
      name,
      selected: false,
      options: services
        .filter((s) => s.attributes?.["service-type"] === serviceType)
        .flatMap(toOptions),
      columns,
    }));
  }, [data]);

  return { sections, isLoading: isFetching, error };
};

export default useParentLines;
